import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared chrome for the public (unauthenticated) pages: a side panel (contact info, accepted
 * payment methods — same slot Magetsi uses for a promo banner, ours is just informational) and a
 * footer. Own branding and copy throughout — nothing copied from any reference site.
 */
export function SidePanel() {
  return (
    <aside style={{
      background: '#1e2460', color: 'white', borderRadius: 14, padding: 24,
      height: 'fit-content',
    }}>
      <p style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, color: '#93c5fd', marginBottom: 8 }}>
        Need help?
      </p>
      <p style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Talk to us</p>
      <p style={{ fontSize: 14, color: '#c7d2fe', marginBottom: 20 }}>
        Having trouble with a payment? Check its status first — most issues resolve within a few
        minutes.
      </p>
      <Link to="/pay/status" style={{
        display: 'inline-block', padding: '8px 16px', borderRadius: 8, background: 'white',
        color: '#1e2460', fontWeight: 600, fontSize: 13, textDecoration: 'none', marginBottom: 24,
      }}>
        Check transaction →
      </Link>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 16 }}>
        <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#93c5fd', marginBottom: 8 }}>
          Payment methods
        </p>
        <span style={{
          display: 'inline-block', background: 'white', color: '#dc2626', fontWeight: 800,
          fontSize: 13, padding: '4px 10px', borderRadius: 6,
        }}>
          EcoCash
        </span>
      </div>
    </aside>
  );
}

/**
 * The homepage's left intro column: a welcome line, short pitch, accepted payment method, a
 * security reassurance, and a support line. Own copy and the one real payment method we actually
 * support (EcoCash) — not a list of methods we don't take.
 */
export function IntroPanel() {
  return (
    <div style={{ paddingTop: 8 }}>
      <p style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>Welcome to Recharge</p>
      <h2 style={{ fontSize: 30, fontWeight: 800, color: '#1e2460', marginBottom: 16 }}>
        Instant top-ups,<br />zero hassle.
      </h2>
      <p style={{ fontSize: 14, color: '#555', marginBottom: 24, maxWidth: 280 }}>
        Recharge is a self-service payment page for Zimbabwean airtime, bundles, electricity and
        Telone — pay for your own number or someone else's in seconds.
      </p>

      <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#999', marginBottom: 8 }}>
        Pay with
      </p>
      <span style={{
        display: 'inline-block', background: 'white', border: '1px solid #e5e7eb', color: '#dc2626',
        fontWeight: 800, fontSize: 14, padding: '6px 14px', borderRadius: 8, marginBottom: 24,
      }}>
        EcoCash
      </span>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span aria-hidden style={{
          width: 20, height: 20, borderRadius: '50%', background: '#1e2460', color: 'white',
          fontSize: 12, display: 'grid', placeItems: 'center',
        }}>🔒</span>
        <p style={{ fontSize: 13, color: '#444' }}>
          Secure checkout — your details are never stored on this page.
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span aria-hidden style={{
          width: 20, height: 20, borderRadius: '50%', background: '#16a34a', color: 'white',
          fontSize: 12, display: 'grid', placeItems: 'center',
        }}>✓</span>
        <p style={{ fontSize: 13, color: '#444' }}>
          Having trouble? <Link to="/pay/status" style={{ color: '#1e2460', fontWeight: 600 }}>Check your transaction</Link>.
        </p>
      </div>
    </div>
  );
}

/** Dismissible gradient announcement bar — same structural slot as Magetsi's, our own message. */
export function AnnouncementBar() {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('recharge.announcement-dismissed') === '1'; } catch { return false; }
  });
  if (dismissed) return null;
  return (
    <div className="pub-header" style={{
      background: 'linear-gradient(90deg,#1e2460,#2d3577,#1e2460)', color: 'white',
      textAlign: 'center', position: 'relative', fontSize: 13,
    }}>
      <span style={{ fontWeight: 700 }}>Welcome to Recharge.</span>{' '}
      Instant airtime, bundles, electricity and Telone top-ups — paid for with EcoCash.
      <button
        onClick={() => {
          setDismissed(true);
          try { sessionStorage.setItem('recharge.announcement-dismissed', '1'); } catch { /* noop */ }
        }}
        aria-label="Dismiss announcement"
        style={{
          position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', fontSize: 18,
          cursor: 'pointer', lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

const reasons = [
  { icon: '🖱️', title: 'Unrivalled convenience', body: "Pay for airtime, bundles, electricity or Telone any time, from anywhere — no office hours, no queues." },
  { icon: '🔒', title: 'Top-notch security', body: 'Every transaction is processed through EcoCash’s own secure payment rails — we never see or store your PIN.' },
  { icon: '⚡', title: 'Instant delivery', body: "Once payment is confirmed, your recharge is placed immediately — most complete within moments." },
  { icon: '📱', title: 'Works for anyone', body: "Top up your own number or someone else's — no account or sign-up required to pay." },
  { icon: '🧾', title: 'Always trackable', body: "Every payment gets a reference you can check back on later at any time, from any device." },
  { icon: '🤝', title: 'Built in Zimbabwe', body: "Made for Zimbabwean networks and billers, with local support behind every transaction." },
];

/** "Why choose us" section — same structural slot as Magetsi's reasons grid, our own copy. */
export function WhyChooseUs() {
  return (
    <section className="pub-section">
      <h2 style={{ fontSize: 26, fontWeight: 800, textAlign: 'center', color: '#1e2460', marginBottom: 10 }}>
        Why choose Recharge?
      </h2>
      <p style={{ fontSize: 14, color: '#666', textAlign: 'center', maxWidth: 640, margin: '0 auto 32px' }}>
        A payment isn't just a transaction — it's your time, your money and your peace of mind.
        Here's how we've built Recharge around that.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
        {reasons.map((r) => (
          <div key={r.title} style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%', border: '2px solid #1e2460', display: 'grid',
              placeItems: 'center', fontSize: 18, marginBottom: 12,
            }}>
              {r.icon}
            </div>
            <p style={{ fontWeight: 700, fontSize: 15, color: '#111', marginBottom: 6 }}>{r.title}</p>
            <p style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>{r.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/** WhatsApp contact banner — same structural slot as Magetsi's, pointed at our own number (TODO if unset). */
export function WhatsAppBanner({ phone }: { phone?: string }) {
  const href = phone
    ? `https://api.whatsapp.com/send/?phone=${phone}&text=${encodeURIComponent('Hi Recharge')}`
    : undefined;
  return (
    <section className="pub-section" style={{
      background: '#eef0ff', display: 'flex', flexWrap: 'wrap',
      alignItems: 'center', justifyContent: 'space-between', gap: 16,
    }}>
      <div style={{ maxWidth: 520 }}>
        <p style={{ fontSize: 18, fontWeight: 700, color: '#1e2460', marginBottom: 6 }}>
          Need a hand? Talk to us on WhatsApp
        </p>
        <p style={{ fontSize: 13, color: '#555' }}>
          Having trouble with a payment, or want help finding the right bundle? Message us and
          we'll sort it out.
        </p>
      </div>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, background: '#25D366', color: 'white',
          padding: '10px 18px', borderRadius: 8, fontWeight: 600, fontSize: 14, textDecoration: 'none',
        }}>
          💬 Open WhatsApp
        </a>
      ) : (
        <Link to="/pay/status" style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, background: '#1e2460', color: 'white',
          padding: '10px 18px', borderRadius: 8, fontWeight: 600, fontSize: 14, textDecoration: 'none',
        }}>
          Check your transaction instead →
        </Link>
      )}
    </section>
  );
}

const partners = ['EcoCash', 'Econet', 'NetOne', 'Telone', 'ZETDC', 'HOT Recharge'];

/** Partner/network strip — same structural slot as Magetsi's logo row; we name our real upstream partners. */
export function PartnersStrip() {
  return (
    <section className="pub-section" style={{ background: '#f5f6f8' }}>
      <h2 style={{ fontSize: 15, fontWeight: 700, textAlign: 'center', color: '#444', marginBottom: 20 }}>
        Networks &amp; partners we work with
      </h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12 }}>
        {partners.map((p) => (
          <span key={p} style={{
            background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 18px',
            fontSize: 13, fontWeight: 600, color: '#333',
          }}>
            {p}
          </span>
        ))}
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="pub-section" style={{ background: '#13173f', color: '#aab0d8', marginTop: 48 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
        <div>
          <p style={{ color: 'white', fontWeight: 700, fontSize: 16, marginBottom: 6 }}>⚡ Recharge</p>
          <p style={{ fontSize: 12, maxWidth: 260 }}>
            Airtime, bundles, electricity and Telone top-ups, paid for instantly with EcoCash.
          </p>
        </div>
        <div>
          <p style={{ color: 'white', fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Quick links</p>
          <FooterLink to="/">Home</FooterLink>
          <FooterLink to="/pay">Recharge now</FooterLink>
          <FooterLink to="/pay/status">Check transaction</FooterLink>
          <FooterLink to="/login">Staff sign in</FooterLink>
        </div>
        <div>
          <p style={{ color: 'white', fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Services</p>
          <p style={{ fontSize: 12, marginBottom: 4 }}>Airtime &amp; bundles</p>
          <p style={{ fontSize: 12, marginBottom: 4 }}>ZESA electricity</p>
          <p style={{ fontSize: 12, marginBottom: 4 }}>Telone</p>
        </div>
      </div>
      <p style={{ textAlign: 'center', fontSize: 11, marginTop: 24, color: '#6b7299' }}>
        © {new Date().getFullYear()} Recharge. All amounts are processed instantly where upstream
        networks allow.
      </p>
    </footer>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} style={{ display: 'block', fontSize: 12, color: '#aab0d8', textDecoration: 'none', marginBottom: 4 }}>
      {children}
    </Link>
  );
}
